import React, { useState } from 'react';
import { config } from '../config/config';

const UserInfoForm = ({ nextStep, previousStep, updatePatientData, appState, setError }) => {
  const [formData, setFormData] = useState({
    name: appState.patientData?.name || '',
    age: appState.patientData?.age || '',
    phone: appState.patientData?.phone || '',
    city: appState.patientData?.city || '',
    state: appState.patientData?.state || ''
  });

  // Indian states list
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Username is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Username must be at least 2 characters';
    }

    // Age validation
    if (!formData.age) {
      errors.age = 'Age is required';
    } else {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum) || ageNum < 13 || ageNum > 120) {
        errors.age = 'Please enter a valid age between 13 and 120';
      }
    }

    // Phone validation for Indian numbers (10 digits)
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        errors.phone = 'Please enter a valid 10-digit Indian phone number';
      }
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = 'City is required';
    } else if (formData.city.trim().length < 2) {
      errors.city = 'Please enter a valid city name';
    }

    // State validation
    if (!formData.state) {
      errors.state = 'State is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the highlighted errors before continuing.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean and format the data
      const cleanedData = {
        name: formData.name.trim(),
        age: parseInt(formData.age),
        phone: formData.phone.trim(),
        city: formData.city.trim(),
        state: formData.state,
        gender: 'not-specified', // Default value for AI analysis
        email: 'not-provided@email.com' // Default value for backwards compatibility
      };

      // If quick mode, add default condition values
      if (appState?.analysisMode === 'quick') {
        cleanedData.primaryConcern = 'normal_skin';
        cleanedData.visitPurpose = 'just_curious';
        cleanedData.conditionDuration = 'not_applicable';
      }

      updatePatientData(cleanedData);
      
      setTimeout(() => {
        setIsSubmitting(false);
        nextStep();
      }, 500);
      
    } catch (error) {
      setIsSubmitting(false);
      setError('Failed to save patient information. Please try again.');
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    // Keep numbers as-is for Indian format, just limit to 10 digits
    return numbers.slice(0, 10);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center py-12">
      <div className="max-w-md mx-auto w-full px-6">
        {/* Progress Indicator */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-500">
              Step 2 of {appState?.analysisMode === 'quick' ? 3 : 4}
            </span>
            <span className="text-sm text-gray-400">
              {appState?.analysisMode === 'quick' ? '67%' : '50%'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div className="bg-gray-900 h-1 rounded-full transition-all duration-500" style={{ width: appState?.analysisMode === 'quick' ? '67%' : '50%' }}></div>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Almost there!
            </h1>
            <p className="text-gray-600">
              Quick details to personalize your skin analysis
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Your Name
              </label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400"
                disabled={isSubmitting}
              />
              {validationErrors.name && (
                <p className="text-sm text-red-600">{validationErrors.name}</p>
              )}
            </div>

            {/* Age Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Age
              </label>
              <input
                type="number"
                placeholder="Enter your age"
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                min="13"
                max="120"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400"
                disabled={isSubmitting}
              />
              {validationErrors.age && (
                <p className="text-sm text-red-600">{validationErrors.age}</p>
              )}
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm font-medium">+91</span>
                </div>
                <input
                  type="tel"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', formatPhoneNumber(e.target.value))}
                  className="w-full pl-14 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400"
                  disabled={isSubmitting}
                  maxLength="10"
                />
              </div>
              {validationErrors.phone && (
                <p className="text-sm text-red-600">{validationErrors.phone}</p>
              )}
            </div>

            {/* City and State Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* City Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  placeholder="e.g., Mumbai"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-400"
                  disabled={isSubmitting}
                />
                {validationErrors.city && (
                  <p className="text-sm text-red-600">{validationErrors.city}</p>
                )}
              </div>

              {/* State Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  State
                </label>
                <select
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200 text-gray-900 bg-white"
                  disabled={isSubmitting}
                >
                  <option value="">Select State</option>
                  {indianStates.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {validationErrors.state && (
                  <p className="text-sm text-red-600">{validationErrors.state}</p>
                )}
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-1 text-sm">Your Privacy Guaranteed</h3>
                  <p className="text-green-800 text-xs leading-relaxed font-medium">
                    <strong>Your photo is never saved.</strong> It's analyzed and immediately deleted from our servers - guaranteed. Only your name and phone are securely stored for appointments.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center"
              >
                {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        Start AI Analysis
                      </>
                )}
              </button>
              
              <button
                type="button"
                onClick={previousStep}
                disabled={isSubmitting}
                className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-lg transition-all duration-200"
              >
                ← Back to Photo
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserInfoForm;
